import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import { alpha } from '@mui/material/styles';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import moment from 'moment';

const AVATAR_COLORS = [
  '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
  '#f57c00', '#0097a7', '#c2185b', '#455a64',
];

function avatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initials(name = '') {
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function formatTimestamp(ts) {
  const m = moment(ts, ['YYYY/MM/DD HH:mm', 'YYYY-MM-DD HH:mm', 'MM-DD-YYYY HH:mm', moment.ISO_8601]);
  if (!m.isValid()) return ts || '';
  const now = moment();
  if (now.diff(m, 'hours') < 24) return m.format('h:mm a');
  if (now.diff(m, 'days') < 7) return m.format('ddd h:mm a');
  return m.format('MMM D, YYYY h:mm a');
}

export default function Notes({ car }) {
  const [notes, setNotes] = React.useState(car.notes || []);
  const [draft, setDraft] = React.useState('');
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const me = StateManager.userName || 'Unknown';
  const canDelete = StateManager.isAdmin() || StateManager.isManager();

  const addNote = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    const note = {
      value: trimmed,
      timestamp: moment().format('YYYY/MM/DD HH:mm'),
      author: me,
    };
    const updated = [...notes, note];
    setNotes(updated);
    setDraft('');
    StateManager.updateCar({ notes: updated });
    await firebase.firestore().doc('cars/' + car.stock).update({ notes: updated });
  };

  const deleteNote = async (index) => {
    if (!window.confirm('Delete this note?')) return;
    const updated = notes.filter((_, i) => i !== index);
    setNotes(updated);
    StateManager.updateCar({ notes: updated });
    await firebase.firestore().doc('cars/' + car.stock).update({ notes: updated });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote();
  };

  return (
    <Box sx={{ mx: -2, mt: -2, display: 'flex', flexDirection: 'column' }}>

      {/* Notes feed */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {notes.length === 0 ? (
          <Box sx={{ py: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>No notes yet — add the first one below</Typography>
          </Box>
        ) : (
          notes.map((note, i) => {
            const author = note.author || 'Unknown';
            const isMe = author === me;
            const color = avatarColor(author);

            return (
              <Box
                key={i}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  flexDirection: isMe ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                }}
              >
                <Tooltip title={author} placement={isMe ? 'left' : 'right'}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: color, flexShrink: 0, mt: 0.25 }}>
                    {initials(author)}
                  </Avatar>
                </Tooltip>

                <Box sx={{ maxWidth: '72%', minWidth: 0 }}>
                  {/* Author + timestamp */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.35, flexDirection: isMe ? 'row-reverse' : 'row' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
                      {isMe ? 'You' : author}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11, lineHeight: 1 }}>
                      {formatTimestamp(note.timestamp)}
                    </Typography>
                  </Box>

                  {/* Bubble + delete */}
                  <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.75, py: 1.1,
                        bgcolor: isMe ? color : 'grey.100',
                        color: isMe ? 'white' : 'text.primary',
                        borderRadius: isMe ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
                        display: 'inline-block',
                        maxWidth: '100%',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>
                        {note.value}
                      </Typography>
                    </Paper>

                    {canDelete && hoveredIndex === i && (
                      <IconButton
                        size="small"
                        onClick={() => deleteNote(i)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          [isMe ? 'left' : 'right']: -8,
                          width: 22, height: 22,
                          bgcolor: 'background.paper',
                          boxShadow: 2,
                          color: 'text.secondary',
                          '&:hover': { color: 'error.main', bgcolor: (t) => alpha(t.palette.error.main, 0.08) },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Compose area */}
      <Box sx={{
        position: 'sticky',
        bottom: 0,
        px: { xs: 2, sm: 3 },
        py: 1.75,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-end',
      }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: avatarColor(me), flexShrink: 0, mb: 0.5 }}>
          {initials(me)}
        </Avatar>
        <TextField
          fullWidth
          multiline
          maxRows={5}
          size="small"
          placeholder="Add a note… (⌘↵ to send)"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <IconButton
          color="primary"
          onClick={addNote}
          disabled={!draft.trim()}
          sx={{ flexShrink: 0, mb: 0.25 }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
